import { log } from '../app/logger.js';
import { window } from '../window.js';

/**
 * Returns a type of a string.
 */
const typeOf = value => {
  let el = window.document.createElement('input');
  el.type = 'url';
  el.value = value;
  if (el.checkValidity()) {
    return 'url';
  }

  el.type = 'email';
  el.value = value;
  if (el.checkValidity()) {
    return 'email';
  }

  return 'string';
};

/**
 * Loads a style.
 */
const loadStyle = (url, integrity) => {
  let el = window.document.createElement('link');
  el.rel = 'stylesheet';
  if (integrity) {
    el.integrity = integrity;
    el.crossOrigin = 'anonymous';
  }
  el.href = url;
  window.document.head.appendChild(el);
};

/**
 * Loads a script.
 */
const loadScript = (url, integrity) =>
  new Promise((resolve, reject) => {
    let el = window.document.createElement('script');
    el.onload = resolve;
    el.onerror = reject;
    el.type = 'text/javascript';
    if (integrity) {
      el.integrity = integrity;
      el.crossOrigin = 'anonymous';
    }
    el.src = url;
    window.document.head.appendChild(el);
  });

/**
 * Adds swipe-left/right event listener.
 */
const addSwipeLeft = (el, listener) => addSwipeListener(el, listener, 'left');
const addSwipeRight = (el, listener) => addSwipeListener(el, listener, 'right');

/**
 * Adds swipe-left event listener.
 */
const addSwipeListener = (el, listener, type) => {
  const swipeHandler = new (class {
    handleEvent(ev) {
      switch (ev.type) {
        case 'touchstart':
          this.#touchStartX = ev.changedTouches[0].clientX;
          this.#touchStartY = ev.changedTouches[0].clientY;
          break;
        case 'touchend':
          if (ev.changedTouches.length == 1) {
            let deltaX = this.#touchStartX - ev.changedTouches[0].clientX;
            let deltaY = Math.abs(
              ev.changedTouches[0].clientY - this.#touchStartY
            );
            if (
              (type == 'left' && deltaY < deltaX) ||
              (type == 'right' && deltaY > deltaX)
            ) {
              listener();
            }
          }
          break;
      }
    }
    #touchStartX;
    #touchStartY;
  })();

  el.addEventListener('touchstart', swipeHandler);
  el.addEventListener('touchend', swipeHandler);
};

/**
 * Helper class to update UI depending on the screen size.
 */
const screenMedia = new (class {
  registerUI(
    cb,
    options = {
      mobile: true,
    }
  ) {
    const { mobile, orientation } = options;
    if (mobile) {
      this.#listenForResize();
    }
    if (orientation) {
      this.#listenForOrientationChange();
    }
    this.#list.push([cb, options]);
  }

  get orientation() {
    return window.screen.orientation && window.screen.orientation.type;
  }

  isMobile() {
    return (
      window.matchMedia('screen and (max-width:480px)').matches ||
      window.matchMedia('screen and (max-height:480px)').matches
    );
  }

  #adjustUI(type, value) {
    for (let [cb, options] of this.#list) {
      if (options[type]) {
        cb(value);
      }
    }
  }

  #listenForResize() {
    if (!this.#resizeOn) {
      window.addEventListener('resize', () => {
        let isMobile = this.isMobile;
        if (isMobile != this.#isMobile) {
          this.#isMobile = isMobile;
          this.#adjustUI('mobile', isMobile);
        }
      });
      this.#resizeOn = true;
    }
  }

  #listenForOrientationChange() {
    if (!this.#orientationChangeOn) {
      window.screen.orientation.addEventListener('change', () => {
        let orientation = this.orientation;
        if (orientation != this.#orientation) {
          this.#orientation = orientation;
          this.#adjustUI('orientation', orientation);
        }
      });
      this.#orientationChangeOn = true;
    }
  }

  #list = [];
  #isMobile = this.isMobile();
  #orientation = this.orientation;

  #resizeOn = false;
  #orientationChangeOn = false;
})();

/**
 * Waits until condition is true or timeout, whichever comes first.
 */
const waitFor = (cond, timeout, msg) => {
  let timeoutid = 0;

  function waitForInternal(cb) {
    let result = cond();
    if (result) {
      return cb(result);
    }
    timeoutid = setTimeout(waitForInternal, 0);
    return null;
  }

  return Promise.race([
    new Promise(resolve => waitForInternal(resolve)),
    new Promise(resolve => setTimeout(resolve, timeout)),
  ]).then(winner => {
    if (winner == 'timeout') {
      clearTimeout(timeoutid);
      log(`${msg} race out`);
    }
    return winner;
  });
};

/**
 * A function that is invoked after timeout, and only once even if was
 * triggered multiple times during the timeout.
 */
class PendingFunction {
  constructor(func, timeout) {
    this.#func = func;
    this.#timeout = timeout;
  }

  schedule() {
    this.cancel();
    this.#handle = window.setTimeout(() => this.invoke(), this.#timeout);
  }

  invoke() {
    this.cancel();
    return this.#func();
  }

  cancel() {
    if (this.#handle) {
      window.clearTimeout(this.#handle);
      this.#handle = 0;
    }
  }

  isScheduled() {
    return !!this.#handle;
  }

  #func;
  #timeout;
  #handle = 0;
}

const logFocusEvents = () => {
  window.document.addEventListener(
    'focus',
    ev => {
      let chain = '';
      for (let node = ev.target; node; node = node.parentNode) {
        let id = (node.id && `#${node.id}`) || '';
        let cn = '';
        if (node.classList) {
          cn = [...node.classList.values()].reduce((a, v) => `${a}.${v}`, '');
        }
        chain = `${node.nodeName}${id}${cn}${(chain && ` > ${chain}`) || ''}`;
      }
      log(`[focus] ${chain}`);
    },
    true
  );
};

export {
  PendingFunction,
  addSwipeLeft,
  addSwipeRight,
  loadStyle,
  loadScript,
  logFocusEvents,
  screenMedia,
  typeOf,
  waitFor,
};
